import {
	BillingStatus,
	ContactTextConsentStatus,
	CreditApplicationStatus,
	CustomerInvoiceStatus,
	CustomerStatus,
	InvoiceStanding,
	InvoiceStatus,
	InvoiceTaxStatus,
	OrderAllocationStatus,
	OrderStatus,
	OtaReleaseStatus,
	OtaRolloutStatus,
	PaymentStatus,
	PaymentSyncStatus,
	QuickBooksExportStatus,
	RampBillDisplayStatus,
	RampPurchaseOrderInvoiceStatus,
	ScheduledInventoryTransferStatus,
	VendorPurchaseOrderStatus
} from "~/gql/graphqlBase.generated";
import { titleCase } from "~/helpers/titleCase";

export type StatusChipColor = "default" | "error" | "info" | "success" | "warning";

export type StatusFamily =
	| "billing"
	| "creditApplication"
	| "customer"
	| "customerInvoice"
	| "invoice"
	| "invoiceStanding"
	| "invoiceTaxStatus"
	| "laborSchedule"
	| "order"
	| "orderAllocation"
	| "orderHold"
	| "otaRelease"
	| "otaRing"
	| "otaRollout"
	| "payment"
	| "paymentSync"
	| "poImportLine"
	| "purchaseOrder"
	| "quickBooksExport"
	| "rampBill"
	| "rampPurchaseOrderInvoice"
	| "scheduledTransfer"
	| "textConsent";

export interface StatusChipDescriptor {
	color: StatusChipColor;
	label: string;
}

const BILLING_STATUS_COLORS: Record<BillingStatus, StatusChipColor> = {
	[BillingStatus.Active]: "success",
	[BillingStatus.Trialing]: "info",
	[BillingStatus.PastDue]: "error",
	[BillingStatus.Canceled]: "default",
	[BillingStatus.Suspended]: "error",
	[BillingStatus.None]: "default"
};

const ORDER_STATUS_COLORS: Record<OrderStatus, StatusChipColor> = {
	[OrderStatus.Initialized]: "default",
	[OrderStatus.Open]: "info",
	[OrderStatus.Submitted]: "warning",
	[OrderStatus.Acknowledged]: "info",
	[OrderStatus.Picked]: "info",
	[OrderStatus.Shipped]: "success",
	[OrderStatus.InTransit]: "info",
	[OrderStatus.Delivered]: "success",
	[OrderStatus.FailedDelivery]: "warning",
	[OrderStatus.Closed]: "success",
	[OrderStatus.Synchronized]: "success",
	[OrderStatus.Cancelled]: "error",
	[OrderStatus.Deleted]: "error",
	[OrderStatus.SyncError]: "error"
};

const PURCHASE_ORDER_STATUS_COLORS: Record<VendorPurchaseOrderStatus, StatusChipColor> = {
	[VendorPurchaseOrderStatus.Draft]: "default",
	[VendorPurchaseOrderStatus.Unconfirmed]: "default",
	[VendorPurchaseOrderStatus.Submitted]: "default",
	[VendorPurchaseOrderStatus.Confirmed]: "success",
	[VendorPurchaseOrderStatus.Approved]: "success",
	[VendorPurchaseOrderStatus.Shipped]: "warning",
	[VendorPurchaseOrderStatus.Delivered]: "info",
	[VendorPurchaseOrderStatus.Closed]: "info",
	[VendorPurchaseOrderStatus.Cancelled]: "error"
};

const CUSTOMER_STATUS_COLORS: Record<CustomerStatus, StatusChipColor> = {
	[CustomerStatus.Active]: "success",
	[CustomerStatus.Inactive]: "default",
	[CustomerStatus.Initialized]: "default",
	[CustomerStatus.Deleted]: "error",
	[CustomerStatus.Unknown]: "default"
};

const ORDER_ALLOCATION_STATUS_COLORS: Record<OrderAllocationStatus, StatusChipColor> = {
	[OrderAllocationStatus.Allocated]: "success",
	[OrderAllocationStatus.MakeToOrder]: "info",
	[OrderAllocationStatus.NotStarted]: "default",
	[OrderAllocationStatus.Partial]: "warning",
	[OrderAllocationStatus.Short]: "error"
};

const ORDER_HOLD_COLORS: Record<"onHold", StatusChipColor> = {
	onHold: "warning"
};

const OTA_RELEASE_STATUS_COLORS: Record<OtaReleaseStatus, StatusChipColor> = {
	[OtaReleaseStatus.Draft]: "default",
	[OtaReleaseStatus.Published]: "success",
	[OtaReleaseStatus.Halted]: "error"
};

const OTA_RING_STATE_COLORS: Record<"current" | "opened" | "pending" | "skipped", StatusChipColor> = {
	current: "info",
	opened: "success",
	pending: "default",
	skipped: "warning"
};

const OTA_ROLLOUT_STATUS_COLORS: Record<OtaRolloutStatus, StatusChipColor> = {
	[OtaRolloutStatus.Active]: "info",
	[OtaRolloutStatus.Paused]: "warning",
	[OtaRolloutStatus.Completed]: "success",
	[OtaRolloutStatus.Halted]: "error",
	[OtaRolloutStatus.Aborted]: "default"
};

const CUSTOMER_INVOICE_STATUS_COLORS: Record<CustomerInvoiceStatus, StatusChipColor> = {
	[CustomerInvoiceStatus.Paid]: "success",
	[CustomerInvoiceStatus.Payable]: "warning",
	[CustomerInvoiceStatus.Unavailable]: "default",
	[CustomerInvoiceStatus.Voided]: "error"
};

const INVOICE_STATUS_COLORS: Record<InvoiceStatus, StatusChipColor> = {
	[InvoiceStatus.Issued]: "info",
	[InvoiceStatus.Voided]: "error"
};

const INVOICE_STANDING_COLORS: Record<InvoiceStanding, StatusChipColor> = {
	[InvoiceStanding.Open]: "warning",
	[InvoiceStanding.PartiallyPaid]: "info",
	[InvoiceStanding.Paid]: "success",
	[InvoiceStanding.Void]: "error"
};

const INVOICE_TAX_STATUS_COLORS: Record<InvoiceTaxStatus, StatusChipColor> = {
	[InvoiceTaxStatus.Calculated]: "success",
	[InvoiceTaxStatus.NotCalculated]: "warning",
	[InvoiceTaxStatus.Unknown]: "default"
};

export const CREDIT_APPLICATION_STATUS_COLORS: Record<CreditApplicationStatus, StatusChipColor> = {
	[CreditApplicationStatus.Unapplied]: "warning",
	[CreditApplicationStatus.PartiallyApplied]: "info",
	[CreditApplicationStatus.Applied]: "success"
};

const QUICK_BOOKS_EXPORT_STATUS_COLORS: Record<QuickBooksExportStatus, StatusChipColor> = {
	[QuickBooksExportStatus.NotExported]: "default",
	[QuickBooksExportStatus.Pending]: "warning",
	[QuickBooksExportStatus.Sent]: "success",
	[QuickBooksExportStatus.Failed]: "error"
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, StatusChipColor> = {
	[PaymentStatus.Unpaid]: "default",
	[PaymentStatus.PartiallyPaid]: "warning",
	[PaymentStatus.Paid]: "success",
	[PaymentStatus.Overpaid]: "info"
};

export const PAYMENT_SYNC_STATUS_COLORS: Record<PaymentSyncStatus, StatusChipColor> = {
	[PaymentSyncStatus.Completed]: "success",
	[PaymentSyncStatus.FailedPermanent]: "error",
	[PaymentSyncStatus.FailedTransient]: "warning",
	[PaymentSyncStatus.NotApplicable]: "default",
	[PaymentSyncStatus.Pending]: "warning",
	[PaymentSyncStatus.RecordedLocallyOnly]: "default"
};

const RAMP_BILL_STATUS_COLORS: Record<RampBillDisplayStatus, StatusChipColor> = {
	[RampBillDisplayStatus.WaitingForApproval]: "default",
	[RampBillDisplayStatus.Approved]: "default",
	[RampBillDisplayStatus.PaymentScheduled]: "default",
	[RampBillDisplayStatus.Paid]: "success",
	[RampBillDisplayStatus.Rejected]: "error",
	[RampBillDisplayStatus.Problem]: "warning",
	[RampBillDisplayStatus.Archived]: "default"
};

const RAMP_PURCHASE_ORDER_INVOICE_STATUS_COLORS: Record<RampPurchaseOrderInvoiceStatus, StatusChipColor> = {
	[RampPurchaseOrderInvoiceStatus.NotInvoiced]: "default",
	[RampPurchaseOrderInvoiceStatus.Invoiced]: "default",
	[RampPurchaseOrderInvoiceStatus.PartlyPaid]: "info",
	[RampPurchaseOrderInvoiceStatus.Paid]: "success"
};

const LABOR_SCHEDULE_STATUS_COLORS: Record<string, StatusChipColor> = {
	Completed: "success",
	InProgress: "warning",
	Mixed: "default",
	Open: "info"
};

const PO_IMPORT_LINE_STATUS_COLORS: Record<string, StatusChipColor> = {
	confirm: "warning",
	cost: "warning",
	material: "warning",
	quantity: "warning",
	ready: "success",
	removed: "default"
};

const TEXT_CONSENT_STATUS_COLORS: Record<ContactTextConsentStatus, StatusChipColor> = {
	[ContactTextConsentStatus.Unknown]: "default",
	[ContactTextConsentStatus.NotAgreed]: "default",
	[ContactTextConsentStatus.WaitingForYes]: "warning",
	[ContactTextConsentStatus.On]: "success",
	[ContactTextConsentStatus.RepliedStop]: "error",
	[ContactTextConsentStatus.Stopped]: "default"
};

const SCHEDULED_TRANSFER_STATUS_COLORS: Record<ScheduledInventoryTransferStatus, StatusChipColor> = {
	[ScheduledInventoryTransferStatus.Scheduled]: "info",
	[ScheduledInventoryTransferStatus.Executed]: "success",
	[ScheduledInventoryTransferStatus.Skipped]: "warning",
	[ScheduledInventoryTransferStatus.Failed]: "error",
	[ScheduledInventoryTransferStatus.Cancelled]: "default",
	[ScheduledInventoryTransferStatus.Unknown]: "default"
};

const FAMILY_STATUS_COLORS: Record<StatusFamily, Record<string, StatusChipColor>> = {
	billing: BILLING_STATUS_COLORS,
	creditApplication: CREDIT_APPLICATION_STATUS_COLORS,
	customer: CUSTOMER_STATUS_COLORS,
	customerInvoice: CUSTOMER_INVOICE_STATUS_COLORS,
	invoice: INVOICE_STATUS_COLORS,
	invoiceStanding: INVOICE_STANDING_COLORS,
	invoiceTaxStatus: INVOICE_TAX_STATUS_COLORS,
	laborSchedule: LABOR_SCHEDULE_STATUS_COLORS,
	order: ORDER_STATUS_COLORS,
	orderAllocation: ORDER_ALLOCATION_STATUS_COLORS,
	orderHold: ORDER_HOLD_COLORS,
	otaRelease: OTA_RELEASE_STATUS_COLORS,
	otaRing: OTA_RING_STATE_COLORS,
	otaRollout: OTA_ROLLOUT_STATUS_COLORS,
	payment: PAYMENT_STATUS_COLORS,
	paymentSync: PAYMENT_SYNC_STATUS_COLORS,
	poImportLine: PO_IMPORT_LINE_STATUS_COLORS,
	purchaseOrder: PURCHASE_ORDER_STATUS_COLORS,
	quickBooksExport: QUICK_BOOKS_EXPORT_STATUS_COLORS,
	rampBill: RAMP_BILL_STATUS_COLORS,
	rampPurchaseOrderInvoice: RAMP_PURCHASE_ORDER_INVOICE_STATUS_COLORS,
	scheduledTransfer: SCHEDULED_TRANSFER_STATUS_COLORS,
	textConsent: TEXT_CONSENT_STATUS_COLORS
};

export const formatStatusLabel = (status: string | null | undefined): string => (status ? titleCase(status) : "");

export const statusChipColor = (family: StatusFamily, status: string | null | undefined): StatusChipColor =>
	(status && FAMILY_STATUS_COLORS[family][status]) || "default";

export const statusChipDescriptor = (
	family: StatusFamily,
	status: string | null | undefined
): StatusChipDescriptor => ({
	color: statusChipColor(family, status),
	label: formatStatusLabel(status)
});
